import React, { useState, useEffect, useRef, useCallback, useMemo, useReducer } from 'react';
import {
  Box,
  Alert,
  Avatar,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip,
  Typography,
  useTheme,
  Snackbar
} from '@mui/material';
import AddCommentOutlinedIcon from '@mui/icons-material/AddCommentOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import ExploreIcon from '@mui/icons-material/Explore';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import CollectionsBookmarkIcon from '@mui/icons-material/CollectionsBookmark';
import AgentDetailModal from './agents/AgentDetailModal';
import AgentReviewDialog from './agents/AgentReviewDialog';
import { Z_INDEX } from '../constants/zIndex';
import { useAgentPreferences } from '../hooks/useAgentPreferences';
import agentReviewService from '../services/agentReviewService';
import { useNavigate, useLocation } from 'react-router-dom';
import { useWebSocket } from '../hooks/useWebSocket';
import { useTurnLifecycle } from '../hooks/useTurnLifecycle';
import authService from '../services/authService';
import threadService from '../services/threadService';
import config from '../config';
import { useChatState } from '../context/ChatStateContext';
import { useAuth } from '../context/AuthContext';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
// Question components removed - feature deprecated
import { ChatFileUploader } from './chat/upload/ChatFileUploader.tsx';
import { chatFileOptions } from './chat/upload/ChatFileHandler';
import { ChatInput } from './chat/ChatInput';
import { useAudioRecordingWithServer } from '../hooks/useAudioRecording';
import ChatMessages from './chat/ChatMessages';
import { useThread } from '../context/ThreadContext';
import { useMessages } from '../hooks/useMessages';
// useInteractiveQuestions hook removed - feature deprecated
import { useMessageSending } from '../hooks/useMessageSending';
import { useRetryMessage } from '../hooks/useRetryMessage';
import { useN8NDaemonHandler } from '../hooks/useN8NDaemonHandler';
import ChatWelcomeScreen from './chat/ChatWelcomeScreen';
import { useFileUploadWithThumbnails } from '../hooks/useFileUploadWithThumbnails';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import { useRoles } from '../hooks/useRoles';
import { useUserDraftRoles } from '../hooks/useUserDraftRoles';
import { useBilling } from '../context/BillingContext';
import { getInitials } from './editor/features/roles/utils/rolColorUtils';
import { prepareRoleColorForChat } from '../utils/roleColorValidation';
import { buildRagStatusPatch } from '../utils/ragStatusPatch';
import postgresBackendService from '../services/postgresBackendService';
import { streamingBufferReducer } from '../streaming/streamingBufferReducer';
import { debeSeguirElFinal } from '../streaming/scrollPolicy';
import { isFinalAgentMessage } from '../streaming/liveBubblePolicy';
import { cancelledPartialMessage } from '../streaming/cancelledPartialMessage';
import ChatDraftPendingStack from './chat/ChatDraftPendingStack';

const Chat = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { user, userProfile } = useAuth(); // Obtener user de Supabase y perfil de PostgreSQL
  const { processingStatus, setProcessingStatus } = useChatState();
  const { roles: allRoles } = useRoles();
  const [draftError, setDraftError] = useState(null);
  const { hiddenAgentIds, toggleHidden } = useAgentPreferences();
  const [username] = useState('usuario');
  const messagesEndRef = useRef(null);
  const chatInputRef = useRef(null);
  const lastScrolledThreadRef = useRef(null);
  const lastScrolledMessagesRef = useRef(null);
  const [highlightedMessageId, setHighlightedMessageId] = useState(null);
  // Auto-scroll durante streaming: contenedor scrollable + flag "stick-to-bottom".
  const scrollContainerRef = useRef(null);
  const stickToBottomRef = useRef(true);
  // Thread management from context (Sidebar handles thread list UI)
  const {
    threads,
    setThreads,
    currentThread,
    setCurrentThread,
    isThreadListOpen,
    setIsThreadListOpen,
    selectThread,
    clearTargetMessage,
    deleteThread,
    createThread,
    handleNewThread,
    handleThreadTitleUpdated,
    pendingRolId,
    pendingRoleHint,
    setPendingRolId,
    consumePendingRolId,
  } = useThread();

  // Pending drafts surfaced in the chat banner: scoped to the current thread.
  // useUserDraftRoles hides drafts whose draftThreadId !== currentThread.id, so
  // a leftover draft from another conversation never leaks here.
  const {
    drafts: pendingDrafts,
    pendingActions: draftActions,
    publish: publishDraft,
    discard: discardDraft,
    getImpact: getDraftImpact,
  } = useUserDraftRoles({
    threadId: currentThread?.id || null,
    onError: (err, ctx) => {
      const msg = ctx?.action === 'publish'
        ? `Error al publicar: ${err.message || err}`
        : ctx?.action === 'discard'
          ? `Error al descartar: ${err.message || err}`
          : `Error con el borrador: ${err.message || err}`;
      setDraftError(msg);
    },
  });

  // Defense in depth: even if the hook is correctly scoped by threadId, also
  // hide any draft whose rolId is the agent currently open in /agents/builder/:rolId
  // — that surface already shows the same publish/discard controls inline.
  const draftsToShow = useMemo(() => {
    if (!pendingDrafts || pendingDrafts.length === 0) return [];
    const builderMatch = location.pathname.match(/^\/agents\/builder\/([^/]+)/);
    const builderRolId = builderMatch ? builderMatch[1] : null;
    if (!builderRolId) return pendingDrafts;
    return pendingDrafts.filter((d) => d.id !== builderRolId);
  }, [pendingDrafts, location.pathname]);

  const handleViewDraft = useCallback((rolId) => {
    if (rolId) navigate(`/agents/builder/${rolId}`);
  }, [navigate]);

  // Active agent: from pending selection (welcome) or from current thread metadata
  const activeRolId = pendingRolId || currentThread?.metadata?.rolId || null;
  // useRoles() has no shared cache, so arriving here from another route (e.g.
  // /explore) remounts Chat with allRoles empty for one render + fetch. Falling
  // back to the hint the caller stamped avoids flashing the generic welcome
  // header before the agent resolves (#401). allRoles wins once it loads.
  const activeAgent = useMemo(
    () => {
      if (!activeRolId) return null;
      return allRoles.find((r) => r.id === activeRolId)
        || (pendingRoleHint?.id === activeRolId ? pendingRoleHint : null);
    },
    [activeRolId, allRoles, pendingRoleHint]
  );
  // selectedRole = only when pending (welcome screen, no thread yet)
  const selectedRole = pendingRolId ? activeAgent : null;

  // ── Streaming consumer (PRP-B): multi-round buffer driven by 5 WS events ──
  // null = no stream active.
  // Shape when active: { text, preToolText, toolCalls[], agentName, color }.
  // Cleared on thread change, agent-finish, agent_message, cancelled, error.
  const [streamingBuffer, dispatchStreaming] = useReducer(streamingBufferReducer, null);
  // 250 ms lockout window: discard `stream-delta` events that arrive between
  // an `agent-finish`/`cancelled`/`error`/persisted `agent_message` and the
  // bubble being mounted again, to avoid orphan ghost bubbles.
  const lastFinishTsRef = useRef(0);
  // NOTE: agentStatusRef ya existe abajo (line 307) con su useEffect de
  // sincronización; lo reusamos en `onDelta` para sembrar agentName/color
  // como fallback cuando el fork no emite stream-tool-call-start.
  // Snackbar copy for `stream-error`.
  const [streamingError, setStreamingError] = useState(null);

  // Action cards for the active agent (welcome screen starter prompts)
  const [roleActionCards, setRoleActionCards] = useState(null);
  useEffect(() => {
    if (!activeRolId) { setRoleActionCards(null); return; }
    postgresBackendService.getRolActionCards(activeRolId)
      .then(cards => setRoleActionCards(cards?.length ? cards : null))
      .catch(() => setRoleActionCards(null));
  }, [activeRolId]);
  const {
    isConnected,
    isTyping,
    error,
    subscribeToMessages,
    subscribeToStreamingEvents,
    subscribeToFileRagStatus, // #274 — estado RAG por-adjunto de chat
    // currentQuestion, answerQuestion removed - feature deprecated
    agents,
    agentStatus,
    setAgentStatusManually,
    newlyCreatedThreadData,
    clearNewlyCreatedThreadData,
    waitForConnection, // ✅ CRITICAL: Function to wait for WebSocket readiness
    // ✅ FIX: Reconnection tracking for message recovery
    hasReconnected,
    clearReconnectedFlag,
    // ✅ Watchdog timeout (N8N stopped communicating)
    watchdogTimeout,
    clearWatchdogTimeout
  } = useWebSocket(currentThread?.id, username, handleThreadTitleUpdated); // ✅ Pass title update callback

  // ✅ CRITICAL: New React Query hook for message management
  // Replaces custom useChatMessages with battle-tested solution
  // Automatically handles reconciliation, deduplication, and background sync
  const {
    messages,
    isLoading: isLoadingMessages,
    addMessageFromWebSocket,
    refetch: loadMessages,
    isSending,
    sendMessage, // ✅ NEW: Expose sendMessage for useMessageSending
    updateMessageFeedback,
    updateAttachmentRagStatus, // #274 — parcha el estado RAG de un adjunto por fileId
    applyStoppedPlan, // #898 — pinta el contador del plan cortado sobre el mensaje que abrió el turno
    initialAgentStatus // ✅ PHASE 4: Get initial agentStatus from backend
  } = useMessages(currentThread?.id);

  // --- Estados para Carga de Archivos ---
  // ✅ Use hook with automatic client-side thumbnail generation
  // #447 — ESTE es el hook por el que pasan las TRES vías de adjuntar (botón "+",
  // arrastrar y pegar): ChatInput.handleDrop/handlePaste llaman directamente a su
  // `handleFileSelect`, saltándose ChatFileUploader. Antes se configuraba a mano con
  // `{ accept, maxSize: 50MB }`, sin validadores custom → arrastrar/pegar no aplicaban
  // los límites por tipo (un .txt de 6,5MB entraba, límite real 5MB) ni comprobación de
  // tipo alguna. Ahora recibe `chatFileOptions`, la misma fuente de verdad que el botón,
  // así que las tres vías validan igual. El `accept` anterior además era código muerto:
  // el único <input type="file"> del chat vive en ChatFileUploader.
  const fileUploadHook = useFileUploadWithThumbnails(
    async (files) => {
      console.log('[Chat] Files selected with thumbnails:', files.length);
    },
    chatFileOptions
  );

  const {
    filesWithThumbnails,
    isGeneratingThumbnails,
    triggerFileSelect,
    clearFiles,
    restoreFiles,
    removeFile,
    fileInputRef,
    handleFileSelect,
    error: fileSelectError
  } = fileUploadHook;
  // --- Agent dropdown menu state ---
  const [agentMenuAnchor, setAgentMenuAnchor] = useState(null);
  const [agentMenuPendingNav, setAgentMenuPendingNav] = useState(null);
  const [aboutModalOpen, setAboutModalOpen] = useState(false);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [existingReview, setExistingReview] = useState(null);
  const [reviewSummary, setReviewSummary] = useState(null);
  // Sin crédito el backend rechaza el envío con un 402, así que el botón se
  // pinta apagado y al pulsarlo abre el aviso en vez de intentarlo (#1300).
  const { isExhausted, openLimitNotice } = useBilling();

  // Borradores por hilo (en sesión, sin localStorage)
  const [drafts, setDrafts] = useState({});
  // Se incrementa para pedirle al input que reponga el borrador sin cambiar de
  // hilo. Ver `restoreDraft` más abajo.
  const [draftRestoreNonce, setDraftRestoreNonce] = useState(0);

  /**
   * Devuelve al input lo que el usuario había escrito y adjuntado cuando el
   * envío falla. Se limpia ANTES de enviar —para que la tarjeta de progreso no
   * salga duplicada con los adjuntos del input—, así que sin esto un rechazo
   * del backend se lleva el mensaje entero (#1299).
   *
   * El orden importa: primero el borrador, luego el contador, porque el efecto
   * del input lee el borrador al reaccionar al contador.
   */
  const restoreDraft = useCallback((text, files) => {
    const threadId = currentThread?.id;
    if (threadId && text) {
      setDrafts(prev => ({ ...prev, [threadId]: text }));
      setDraftRestoreNonce(n => n + 1);
    }
    const originals = (files ?? []).map(entry => entry.file).filter(Boolean);
    if (originals.length > 0) restoreFiles(originals);
  }, [currentThread?.id, restoreFiles]);
  // Estado para error de validación de archivos
  const [fileValidationError, setFileValidationError] = useState(null);
  // Estado para error de validación de contexto
  const [contextValidationError, setContextValidationError] = useState(null);
  // Estado para error de subida de archivos
  const [fileUploadError, setFileUploadError] = useState(null);
  // Estado para indicar si se está subiendo archivos
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);
  // ---------------------------------------

  // #447 / #282 — Surface los errores de validación del hook de adjuntos.
  // `useFileUpload` lanza dentro de handleFileSelect y guarda el error en su estado,
  // pero Chat.jsx no lo leía: el fichero se rechazaba y la interfaz "no reaccionaba"
  // (síntoma exacto de #282). Se cablea al Snackbar de fileUploadError, que ya existe.
  // Mismo patrón que ChatFileUploader.tsx:90-96 para su propio hook.
  useEffect(() => {
    if (!fileSelectError) return;
    setFileUploadError(fileSelectError.message);
    const timer = setTimeout(() => setFileUploadError(null), 8000);
    return () => clearTimeout(timer);
  }, [fileSelectError]);

  // --- "Analizando documento..." chip on the latest user message-with-files ---
  // Set true the moment a message with attachments is dispatched.
  // Cleared on the next `agent_stop_typing` WS event (isTyping true → false transition)
  // or after a 60s safety timeout — whichever comes first.
  const [isAnalyzingAttachments, setIsAnalyzingAttachments] = useState(false);
  const analyzingTimeoutRef = useRef(null);
  const previousIsTypingRef = useRef(false);
  // ----------------------------------------------------------------------

  const { t } = useTranslation();
  const [hasUnreadDaemonActivity, setHasUnreadDaemonActivity] = useState(false);

  // --- Manejo de hilos creados por N8N Daemon ---
  useN8NDaemonHandler({
    newlyCreatedThreadData,
    setThreads,
    setHasUnreadDaemonActivity,
    clearNewlyCreatedThreadData
  });
  // ----------------------------------------------

  // --- Máquina STOP compartida (#494, DEC-6: useTurnLifecycle) ---
  // NOTA: debe definirse ANTES de useMessageSending para poder pasar clearStopStateSync.
  // El unlock lo gobierna la confirmación real (stream-cancelled) o la degradación
  // honesta de 1,5 s — el "optimistic unlock ciego" desaparece. turnUnlockRef se
  // rellena tras useMessageSending (necesita clearProcessingThreadRef).
  const turnUnlockRef = useRef(() => {});
  const {
    isStopping: isStoppingExecution,
    hasStopped: hasStoppedExecution,
    requestStop,
    confirmCancelled,
    clearStopState: clearStopStateSync,
    setHasStopped: setHasStoppedExecution,
  } = useTurnLifecycle({
    threadId: currentThread?.id,
    onUnlock: () => turnUnlockRef.current(),
    onStopError: () => {
      setFileUploadError('Error al detener la ejecución. Intenta de nuevo.');
      setTimeout(() => setFileUploadError(null), 5000);
    },
  });

  // Hook para manejar el envío de mensajes
  const {
    handleSendMessage,
    isAgentCurrentlyActive: isAgentActiveRaw,  // #898: se filtra por hasStoppedExecution abajo
    clearProcessingThreadRef,  // ✅ Function to clear processing ref immediately
    isTransitioningToFirstMessageRef,  // ✅ FIX: Ref to prevent welcome screen flash
    uploadingBatch,            // Per-batch upload progress + status
    cancelUploadingBatch,      // Aborts the in-flight XHR
    clearUploadingBatch,       // Dismisses error state without aborting
  } = useMessageSending({
    agentStatus,
    filesWithThumbnails, // ✅ CHANGED: Pass filesWithThumbnails instead of selectedFiles
    clearFiles,          // ✅ CHANGED: Pass clearFiles function instead of setSelectedFiles
    restoreDraft,        // Repone texto y adjuntos si el envío falla (#1299)
    currentThread,
    setCurrentThread,
    setThreads,
    setProcessingStatus,
    processingStatus,
    isUploadingFiles,
    setContextValidationError,
    setFileUploadError,
    sendMessageFn: sendMessage, // ✅ REPLACED: chatMessagesHook → sendMessage from useMessages
    waitForWebSocketConnection: waitForConnection, // ✅ CRITICAL: Pass WebSocket wait function
    // ✅ STOP state cleanup - new message invalidates previous STOP request
    clearStopStateSync,  // ✅ SIMPLIFIED: Single function that updates state AND ref synchronously
    pendingRolId,
    consumePendingRolId,
  });

  /**
   * #898: un corte confirmado GANA a cualquier señal de actividad del motor.
   *
   * El motor N8N sigue vivo después del ⏹ y sigue llamando a
   * `/webhook/update_agent_status`. Cuando ese ping esquiva el supresor del
   * backend, `agentStatus` revive y con él la primera fuente de
   * `isAgentCurrentlyActive` (la triple fuente de useMessageSending). Antes eso reactivaba
   * la UI entera —se borraba el pie ⏹ con su contador de pasos (#519), volvía
   * el indicador de «escribiendo» y el input se bloqueaba— y el usuario veía
   * exactamente lo que no había pasado: al agente ignorando el stop.
   *
   * El corte lo materializó el backend; el motor no puede desdecirlo. Solo lo
   * apagan las acciones EXPLÍCITAS del usuario (mensaje nuevo y RETRY llaman a
   * clearStopStateSync) y el cambio de hilo (efecto por threadId de
   * useTurnLifecycle). Es DEC-6 de #494.
   *
   * Un único gate porque de este valor cuelgan los tres síntomas: el
   * «escribiendo» (ChatMessages isTyping), el bloqueo del input y el guard del
   * RETRY en useRetryMessage.
   */
  const isAgentCurrentlyActive = isAgentActiveRaw && !hasStoppedExecution;

  // --- Hook extendido de grabación de audio (pro) ---
  const {
    isRecording: audioRecording_isRecording,
    permissionNeeded: audioRecording_permissionNeeded,
    audioError: audioRecording_audioError,
    isProcessingAudio: audioRecording_isProcessingAudio,
    startRecording: audioRecording_startRecording,
    stopRecording: audioRecording_stopRecording,
    clearAudioError: audioRecording_clearAudioError,
    clearPermissionNeeded: audioRecording_clearPermissionNeeded,
    setProcessingAudio: audioRecording_setProcessingAudio,
    sendAudioToServer: audioRecording_sendAudioToServer,
  } = useAudioRecordingWithServer(
    currentThread,
    setCurrentThread,
    setThreads,
    addMessageFromWebSocket,
    setProcessingStatus,
    processingStatus
  );
  // -------------------------------------------

  // Interactive questions feature removed - deprecated
  // const { handleAnswerQuestion, isLastMessageUnansweredQuestion, isInputDisabled } = ...
  const isInputDisabled = false; // Always allow input (no questions blocking)

  // ✅ File handling is now managed by useFileUploadWithThumbnails hook
  // handleFileSelect and removeFile methods are provided by the hook

  // ✅ Refs para acceder a valores actuales en closures sin causar re-suscripciones
  const agentStatusRef = useRef(agentStatus);
  const processingStatusRef = useRef(processingStatus);

  // #494: el unlock del STOP (antes "optimistic unlock") — ahora corre SOLO con
  // confirmación (o degradación honesta). Ref reasignada en cada render para
  // capturar los valores frescos.
  turnUnlockRef.current = () => {
    console.log(`[Chat] 🔓 UNLOCK confirmado para thread ${currentThread?.id}`);
    clearProcessingThreadRef();
    if (currentThread?.id) {
      setProcessingStatus(prev => ({ ...prev, [currentThread.id]: false }));
    }
    setAgentStatusManually({ agentName: '', currentTask: '', color: '' });
  };

  // Mantener refs sincronizados con estados (sin causar re-renders)
  useEffect(() => {
    agentStatusRef.current = agentStatus;
    processingStatusRef.current = processingStatus;
  }, [agentStatus, processingStatus]);

  // --- Hook para retry de último mensaje (MOVED BEFORE handleStopExecution) ---
  const { handleRetry, isRetrying } = useRetryMessage({
    currentThread,
    isAgentCurrentlyActive,
    setFileUploadError,
    isStoppingExecution,  // ✅ PHASE 1: Pass STOP state to RETRY hook
    clearStopState: clearStopStateSync  // #898: el RETRY limpia el corte por sí mismo (ya no lo hace la heurística)
  });
  // ---------------------------------------

  // #494: la máquina STOP vive en useTurnLifecycle — aquí solo queda el guard
  // cross-op contra un RETRY en curso (el debounce y la confirmación son del hook).
  const handleStopExecution = useCallback(async () => {
    if (isRetrying) {
      console.log('[Chat] ❌ STOP bloqueado: RETRY en progreso');
      return;
    }
    await requestStop();
  }, [isRetrying, requestStop]);
  // ---------------------------------------

  // --- Agent dropdown menu handlers ---
  const handleAgentMenuOpen = useCallback((event) => {
    setAgentMenuAnchor(event.currentTarget);
  }, []);
  const handleAgentMenuClose = useCallback(() => {
    setAgentMenuAnchor(null);
  }, []);
  const handleAgentMenuExited = useCallback(() => {
    if (agentMenuPendingNav) {
      navigate(agentMenuPendingNav);
      setAgentMenuPendingNav(null);
    }
  }, [agentMenuPendingNav, navigate]);
  const handleAgentMenuNewChat = useCallback(() => {
    setAgentMenuAnchor(null);
    setPendingRolId(activeAgent?.id);
    handleNewThread();
  }, [activeAgent?.id, setPendingRolId, handleNewThread]);
  const handleAgentMenuAbout = useCallback(() => {
    setAgentMenuAnchor(null);
    setAboutModalOpen(true);
  }, []);
  const handleAgentMenuEdit = useCallback(() => {
    setAgentMenuAnchor(null);
    setAgentMenuPendingNav(`/agents/builder/${activeAgent?.id}`);
  }, [activeAgent?.id]);
  const handleAgentMenuCollections = useCallback(() => {
    setAgentMenuAnchor(null);
    setAgentMenuPendingNav(`/editor/knowledge/${activeAgent?.id}`);
  }, [activeAgent?.id]);
  const handleAgentMenuGallery = useCallback(() => {
    setAgentMenuAnchor(null);
    setAgentMenuPendingNav('/explore');
  }, []);
  const handleAgentMenuToggleHidden = useCallback(() => {
    setAgentMenuAnchor(null);
    if (activeAgent?.id) toggleHidden(activeAgent.id);
  }, [activeAgent?.id, toggleHidden]);
  const handleAgentMenuReview = useCallback(async () => {
    setAgentMenuAnchor(null);
    if (activeAgent?.id) {
      try {
        const review = await agentReviewService.getMyReview(activeAgent.id);
        setExistingReview(review);
      } catch { setExistingReview(null); }
    }
    setReviewDialogOpen(true);
  }, [activeAgent?.id]);
  const handleReviewSubmit = useCallback(async ({ rating, comment }) => {
    if (!activeAgent?.id) return;
    await agentReviewService.upsertReview(activeAgent.id, { rating, comment });
    const summary = await agentReviewService.getReviewSummary(activeAgent.id);
    setReviewSummary(summary);
    setReviewDialogOpen(false);
  }, [activeAgent?.id]);
  const handleReviewDelete = useCallback(async () => {
    if (!activeAgent?.id) return;
    await agentReviewService.deleteReview(activeAgent.id);
    const summary = await agentReviewService.getReviewSummary(activeAgent.id);
    setReviewSummary(summary);
    setExistingReview(null);
    setReviewDialogOpen(false);
  }, [activeAgent?.id]);
  // Fetch review summary when agent changes
  useEffect(() => {
    if (activeAgent?.id) {
      agentReviewService.getReviewSummary(activeAgent.id)
        .then(setReviewSummary)
        .catch(() => setReviewSummary(null));
    } else {
      setReviewSummary(null);
    }
  }, [activeAgent?.id]);
  // ---------------------------------------

  // Thread selection and management now handled by Sidebar via ThreadContext
  // Clear agent status when creating new thread (via keyboard shortcut)
  const handleNewThreadLocal = useCallback(() => {
    setPendingRolId(null);
    handleNewThread();
    setAgentStatusManually(null);
  }, [setPendingRolId, handleNewThread, setAgentStatusManually]);

  // --- Keyboard shortcuts ---
  useKeyboardShortcuts({
    // Cmd+K is now handled globally by AppShell → opens SearchModal
    onStop: useCallback(() => {
      console.log('[Chat] ⌨️  Keyboard shortcut: Stop (Esc)');
      handleStopExecution();
    }, [handleStopExecution]),
    onFocusInput: useCallback(() => {
      console.log('[Chat] ⌨️  Keyboard shortcut: Focus Input (/)');
      chatInputRef.current?.focus();
    }, []),
    canStop: isAgentCurrentlyActive && !isStoppingExecution,
    enabled: true
  });
  // ---------------------------------------

  // Callback estable para guardar borrador del hilo activo
  const handleDraftChange = useCallback((text) => {
    setDrafts(prev => {
      if (!currentThread?.id) return prev;
      return { ...prev, [currentThread.id]: text };
    });
  }, [currentThread?.id]);

  // Wrap handleSendMessage so a message with attachments flips the "Analizando
  // documento..." chip on the bubble, dismissed by `agent_stop_typing` WS event
  // (see useEffect on isTyping below) or a 60 s safety timeout.
  const handleSendMessageWithAnalyzing = useCallback((message, filesWithThumbnailsParam) => {
    const hasFiles = Array.isArray(filesWithThumbnailsParam) && filesWithThumbnailsParam.length > 0;
    if (hasFiles) {
      setIsAnalyzingAttachments(true);
      if (analyzingTimeoutRef.current) clearTimeout(analyzingTimeoutRef.current);
      analyzingTimeoutRef.current = setTimeout(() => {
        setIsAnalyzingAttachments(false);
        analyzingTimeoutRef.current = null;
      }, 60_000);
    }
    return handleSendMessage(message, filesWithThumbnailsParam);
  }, [handleSendMessage]);

  // Listen for the WS `agent_stop_typing` (isTyping true → false) and clear the chip.
  useEffect(() => {
    if (previousIsTypingRef.current && !isTyping && isAnalyzingAttachments) {
      setIsAnalyzingAttachments(false);
      if (analyzingTimeoutRef.current) {
        clearTimeout(analyzingTimeoutRef.current);
        analyzingTimeoutRef.current = null;
      }
    }
    previousIsTypingRef.current = isTyping;
  }, [isTyping, isAnalyzingAttachments]);

  // Cleanup timer on unmount
  useEffect(() => () => {
    if (analyzingTimeoutRef.current) clearTimeout(analyzingTimeoutRef.current);
  }, []);

  const chatInputProps = useMemo(() => ({
    onSend: handleSendMessageWithAnalyzing, // wraps handleSendMessage to drive the analyzing chip
    disabled: isInputDisabled,
    placeholder: t('inputPlaceholder'),
    filesWithThumbnails, // ✅ CHANGED: Pass filesWithThumbnails from hook
    onFilesChange: handleFileSelect, // ✅ CHANGED: Use hook's handleFileSelect
    onRemoveFile: removeFile, // ✅ CHANGED: Use hook's removeFile
    onValidationError: setFileValidationError,
    isRecording: audioRecording_isRecording,
    isProcessingAudio: audioRecording_isProcessingAudio,
    onStartRecording: audioRecording_startRecording,
    onStopRecording: audioRecording_stopRecording,
    audioError: audioRecording_audioError,
    permissionNeeded: audioRecording_permissionNeeded,
    isAgentCurrentlyActive,
    onStop: handleStopExecution,
    isStoppingExecution,
    isRetrying, // ✅ PHASE 6: Pass isRetrying to ChatInput for STOP button state
    fileValidationError,
    contextValidationError,
    fileUploadError,
    isChatGloballyDisabled: isAgentCurrentlyActive,
    // ── Moved from inline JSX to useMemo for React.memo compatibility ──
    threadId: currentThread?.id,
    savedDraft: drafts[currentThread?.id] ?? '',
    restoreNonce: draftRestoreNonce,
    creditBlocked: isExhausted,
    onCreditBlocked: openLimitNotice,
    onDraftChange: handleDraftChange,
    // Upload progress UI (drives the bar/% overlay on each preview card)
    uploadingBatch,
    onCancelUpload: cancelUploadingBatch,
    onDismissUploadError: clearUploadingBatch,
  }), [
    handleSendMessageWithAnalyzing,
    isInputDisabled,
    draftRestoreNonce,
    isExhausted,
    openLimitNotice,
    t,
    filesWithThumbnails, // ✅ CHANGED: Dependency updated
    handleFileSelect, // ✅ CHANGED: Dependency updated
    removeFile, // ✅ CHANGED: Dependency updated
    uploadingBatch,
    cancelUploadingBatch,
    clearUploadingBatch,
    audioRecording_isRecording,
    audioRecording_isProcessingAudio,
    audioRecording_startRecording,
    audioRecording_stopRecording,
    audioRecording_audioError,
    audioRecording_permissionNeeded,
    isAgentCurrentlyActive,
    handleStopExecution,
    isStoppingExecution,
    isRetrying, // ✅ PHASE 6: Add isRetrying to dependency array
    fileValidationError,
    contextValidationError,
    fileUploadError,
    currentThread?.id,
    drafts,
    handleDraftChange,
  ]);

  // #494: el failsafe "agente inactivo con STOP pendiente ⇒ afirmar corte"
  // desaparece — era el sucedáneo de confirmación del mundo optimista. La
  // confirmación real es stream-cancelled, con degradación honesta a 1,5 s
  // dentro de useTurnLifecycle.

  // Limpiar archivos adjuntos al cambiar de thread (el estado STOP lo resetea
  // useTurnLifecycle en su propio efecto por threadId)
  useEffect(() => {
    console.log(`[Chat] 🔄 Thread changed to ${currentThread?.id}, clearing pending files`);
    clearFiles();
  }, [currentThread?.id, clearFiles]);

  // #898: aquí vivía «el agente se reactivó ⇒ limpia hasStoppedExecution». Era
  // una inferencia sobre la intención del usuario a partir de una señal del
  // motor, y el motor zombi la disparaba solo. Los tres caminos que SÍ apagan
  // el corte son explícitos: mensaje nuevo (handleSendMessage de useMessageSending), RETRY
  // (useRetryMessage.js) y cambio de hilo (efecto por threadId de
  // useTurnLifecycle). Si mañana aparece un cuarto, se limpia AHÍ, no aquí.

  // #494: el evento de ACK de stop está RETIRADO (era un no-op en ambas
  // pilas) — la confirmación real es stream-cancelled (useTurnLifecycle).

  // ✅ WATCHDOG TIMEOUT: N8N stopped communicating (crash, OOM, silent failure)
  // Backend detected no activity after timeout and emitted watchdog_timeout event
  useEffect(() => {
    if (!watchdogTimeout || watchdogTimeout.threadId !== currentThread?.id) return;

    console.log(`[Chat] ⏱️ Watchdog timeout for thread ${watchdogTimeout.threadId} — N8N stopped communicating`);

    // Clean up processing state y ofrecer RETRY (el estado isStopping, si lo
    // hubiera, lo resuelve la degradación de 1,5 s de useTurnLifecycle)
    clearProcessingThreadRef();
    setProcessingStatus(prev => ({ ...prev, [currentThread.id]: false }));
    setAgentStatusManually({ agentName: '', currentTask: '', color: '' });
    setHasStoppedExecution(true);
    clearWatchdogTimeout();

    // Notify user that N8N became unresponsive
    setFileUploadError('La operación fue interrumpida por inactividad de N8N');
  }, [watchdogTimeout, currentThread?.id, clearProcessingThreadRef, setProcessingStatus, setAgentStatusManually, clearWatchdogTimeout, setFileUploadError, setHasStoppedExecution]);

  // ✅ CRITICAL: Sincronizar processingStatus con agentStatus
  // SOLO cuando el agente termina (N8N envía agentName="" y currentTask="")
  useEffect(() => {
    if (!currentThread?.id) return;

    // ✅ CRITICAL FIX: Check BOTH name AND task
    // Agent is only active if BOTH exist and are non-empty
    // When N8N sends { agentName: "", currentTask: "" }, both are falsy
    const isAgentActive = Boolean(agentStatus?.agentName && agentStatus?.currentTask);

    // Solo limpiar cuando el agente está inactivo Y había processingStatus
    if (!isAgentActive) {
      setProcessingStatus(prevStatus => {
        const isCurrentlyProcessing = prevStatus[currentThread.id] || false;

        // Solo limpiar si realmente está procesando
        if (isCurrentlyProcessing) {
          // ✅ FIX: Log con valores explícitos para evitar confusión de "undefined" en template strings
          console.log(`[Chat] ✅ Agente terminó, limpiando processingStatus para thread ${currentThread.id}`, {
            agentName: agentStatus?.agentName ?? null,
            currentTask: agentStatus?.currentTask ?? null
          });

          // ✅ FIX: Clear processingThreadRef to prevent 5s delay
          // Without this, isAgentCurrentlyActive stays true via the ref
          // even after agentStatus and processingStatus are cleared
          clearProcessingThreadRef();

          return {
            ...prevStatus,
            [currentThread.id]: false
          };
        }

        return prevStatus;
      });
    }
  }, [agentStatus?.agentName, agentStatus?.currentTask, currentThread?.id, setProcessingStatus, clearProcessingThreadRef]);

  // Threads are now loaded by Sidebar component via ThreadContext

  // ✅ CRITICAL: Explicit message fetch when navigating to EXISTING threads
  // - New thread creation: NO fetch (messages come from optimistic + WebSocket)
  // - Thread selection: YES fetch (load historical messages)
  //
  // NOTE: processingStatus is NOT in dependencies to prevent duplicate fetches
  // when processingStatus changes from true→false after message is sent
  useEffect(() => {
    if (!currentThread?.id) {
      console.log('[Chat] No thread selected, skipping message fetch');
      return;
    }

    // Only fetch if this is an existing thread with potential messages
    // Skip if this thread was just created (will have no messages yet)
    const isMarkedAsProcessing = processingStatus[currentThread.id] === true;

    // ✅ SANITY CHECK: Detect desync state after navigation (e.g., / → /editor → /)
    // If processingStatus says "processing" but we have no messages in cache,
    // the agent likely finished while Chat was unmounted. Clear the stale flag and fetch.
    if (isMarkedAsProcessing && messages.length === 0) {
      console.log(`[Chat] ⚠️ Desync detected: processingStatus=true but messages=0 for thread ${currentThread.id}`);
      console.log('[Chat] 🔄 Clearing stale processingStatus and fetching messages');
      setProcessingStatus(prev => ({
        ...prev,
        [currentThread.id]: false
      }));
      loadMessages();
      return;
    }

    if (isMarkedAsProcessing) {
      console.log('[Chat] Thread is newly created/processing, skipping initial fetch (optimistic update handles it)');
      return;
    }

    console.log(`[Chat] Loading messages for existing thread: ${currentThread.id}`);
    loadMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentThread?.id]);

  // ✅ PHASE 4 + FIX: Sync BOTH agentStatus AND processingStatus with initialAgentStatus from backend
  // When navigating to an existing thread, backend tells us if agent is active
  // This prevents UI desync when switching between active threads
  //
  // ✅ FIX: Also sync agentStatus (for TypingIndicator) not just processingStatus
  // Previously only processingStatus was synced, causing agentStatus to leak between threads
  useEffect(() => {
    if (!currentThread?.id) return;

    // Backend structure from messageService.getThreadAgentStatus():
    // { agentName: 'Copiloto', currentTask: '...', lastUpdated: '...' } or null
    const isAgentActive = Boolean(initialAgentStatus?.agentName && initialAgentStatus?.currentTask);

    if (isAgentActive) {
      console.log(`[Chat] ✅ Thread ${currentThread.id} has active agent, syncing agentStatus and processingStatus`);
      console.log(`[Chat] Agent status from backend:`, {
        agentName: initialAgentStatus.agentName,
        currentTask: initialAgentStatus.currentTask,
        lastUpdated: initialAgentStatus.lastUpdated
      });

      // ✅ FIX: Sync agentStatus for TypingIndicator display
      setAgentStatusManually(initialAgentStatus);

      setProcessingStatus(prev => ({
        ...prev,
        [currentThread.id]: true
      }));
    } else {
      console.log(`[Chat] Thread ${currentThread.id} has no active agent, clearing agentStatus and processingStatus`);

      // ✅ FIX: Clear agentStatus when thread has no active agent
      // This ensures we don't show stale status from a previous thread
      setAgentStatusManually(null);

      // ✅ FIX: Explicitly clear stale processingStatus when backend says no agent is active.
      // Without this, processingStatus[threadId] can remain true in sessionStorage
      // from a previous send — the sync effect (570) depends on agentStatus changes
      // to clear it, but if agentStatus was already null the dep won't change and the
      // clearing never fires, leaving the UI stuck in "processing" state.
      setProcessingStatus(prev => {
        if (prev[currentThread.id]) {
          return { ...prev, [currentThread.id]: false };
        }
        return prev;
      });
      clearProcessingThreadRef();
    }
  }, [currentThread?.id, initialAgentStatus, setProcessingStatus, setAgentStatusManually, clearProcessingThreadRef]);

  // ✅ FIX: Refetch messages after WebSocket reconnection to recover missed messages
  // This handles the race condition where N8N sends messages during reconnection window
  // Works for both network recovery AND token refresh scenarios
  useEffect(() => {
    if (!hasReconnected || !currentThread?.id) return;

    console.log(`[Chat] 🔄 WebSocket reconnected, refetching messages for thread ${currentThread.id}`);

    // Small delay to ensure backend has processed any pending messages
    const timeoutId = setTimeout(() => {
      loadMessages();
      clearReconnectedFlag();
      console.log(`[Chat] ✅ Messages refetched after reconnection`);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [hasReconnected, currentThread?.id, loadMessages, clearReconnectedFlag]);

  // ✅ FIX: Refetch messages when browser tab becomes visible again
  // This handles React Query cache expiry when user switches browser tabs
  // Uses same conservative pattern as WebSocket reconnection above
  //
  // ⚠️⚠️⚠️ FIX iOS-SAFARI ZOMBIE-WS (2026-05-10) — REVERT IF REGRESSION ⚠️⚠️⚠️
  // Before: guarded with `processingStatus[currentThread.id]` (true durante TODA
  //   la ejecución del agente, segundos a minutos). En iOS Safari, cuando el WS
  //   queda zombie en background, esta era la única ruta de recuperación y este
  //   guard la bloqueaba → la respuesta quedaba solo en DB.
  // After: guarded con `isSending` (solo true durante la mutación REST POST,
  //   ventana ~ms). Cubre la race condition original (optimistic + POST en vuelo
  //   + refetch que devuelve vacío) sin bloquear el caso zombie-WS.
  // Race original: useMessages.ts:117-130. Cubre el caso "nuevo thread + envío"
  //   durante el cual un refetch podría machacar el optimistic con `[]`.
  // ROLLBACK: cambiar `isSending` → `processingStatus[currentThread.id]`
  //   y deps `isSending` → `processingStatus`.
  useEffect(() => {
    const handleVisibilityChange = () => {
      // Only act when tab becomes visible (not when hiding)
      if (document.visibilityState !== 'visible') return;

      // Must have a thread selected
      if (!currentThread?.id) return;

      // ⚠️ FIX iOS-Safari (2026-05-10): guard con isSending (mutación REST en vuelo)
      // en lugar de processingStatus (ventana de agente entera). Ver bloque arriba.
      if (isSending) {
        console.log(`[Chat] 👁️ Tab visible but REST mutation in flight, skipping refetch`);
        return;
      }

      console.log(`[Chat] 👁️ Tab became visible, refreshing messages for thread: ${currentThread.id}`);
      loadMessages();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [currentThread?.id, isSending, loadMessages]);

  // Agent status comes via WebSocket, not from loadMessages

  // Suscribirse a mensajes del agente y manejar la recepción
  // ARQUITECTURA SIMPLIFICADA (2025-12):
  // - Los mensajes solo se agregan a la lista, NO liberan la UI
  // - La UI se libera por: update_agent_status vacío O confirmación de STOP (stream-cancelled)
  // - El timeout de 5s en processingThreadRef es el failsafe
  useEffect(() => {
    if (!currentThread?.id || !subscribeToMessages) return;

    console.log(`>>> Suscribiéndose a mensajes para thread: ${currentThread.id}`);

    const handleMessage = (messagePayload) => {
      console.log('>>> Mensaje de agente recibido:', messagePayload);

      // Validar messageId (debe venir del backend)
      if (!messagePayload.messageId) {
        console.error('[Chat] ❌ WebSocket message missing messageId!');
        messagePayload.messageId = `temp-ws-${Date.now()}-${Math.random()}`;
      }

      // Crear objeto mensaje para React Query
      const newMessage = {
        id: messagePayload.messageId,
        thread_id: currentThread?.id,
        content: messagePayload.text,
        sender: 'assistant',
        timestamp: new Date(messagePayload.timestamp),
        message_type: 'text',
        color: messagePayload.color,
        agentName: messagePayload.agentName,
        actions: messagePayload.actions || null
      };

      // Agregar mensaje (React Query maneja deduplicación)
      addMessageFromWebSocket(newMessage);

      setIsAnalyzingAttachments(false);
      if (analyzingTimeoutRef.current) { clearTimeout(analyzingTimeoutRef.current); analyzingTimeoutRef.current = null; }

      // Si el mensaje NO es intermedio, limpiar el status del agente.
      // Evita que el status ("buscando correo") quede stale tras el ultimo mensaje del agente.
      // Fallback: si la IA no marca false, el Broker limpia al final como siempre.
      if (isFinalAgentMessage(messagePayload)) {
        console.log('[Chat] Final message received (isIntermediateMessage=false) - clearing agent status');
        setAgentStatusManually(null);

        // #1130: cerrar la burbuja viva SOLO con el mensaje final. Un intermedio
        // a mitad de turno la vaciaba y armaba el lockout de lastFinishTsRef, que
        // DESCARTA los stream-delta de los 250 ms siguientes: no era un repinte,
        // era texto perdido. Mismo arreglo que useModularChat.ts (#1017 §1), que
        // se hizo solo en esa pila y dio por hecho que aquí ya estaba.
        lastFinishTsRef.current = Date.now();
        dispatchStreaming({ type: 'CLEAR' });
      }

      // ✅ NUEVO (2025-12-27): Activar botón RETRY cuando N8N envía mensaje de error
      // N8N debe enviar isError: true en el payload cuando hay un error
      if (messagePayload.isError === true) {
        console.log(`[Chat] ⚠️ Error message received - enabling RETRY button`);
        setHasStoppedExecution(true);  // Mostrar botón RETRY
      }

      // #494: la máquina STOP la gobierna useTurnLifecycle (confirmación real
      // por stream-cancelled o degradación 1,5 s). Un agent_message ya no
      // limpia el estado de STOP: los mensajes de un turno cancelado se
      // suprimen en el backend y no llegan aquí.

      // NOTA: La UI se libera cuando:
      // 1. Mensaje final (isIntermediateMessage=false) limpia agentStatus (arriba)
      // 2. N8N envía update_agent_status con agentName="" (Broker, fallback)
      // 3. stream-cancelled confirma un STOP (useTurnLifecycle → onUnlock)
      // 4. Timeout de 5s en processingThreadRef expira (failsafe)
    };

    const unsubscribe = subscribeToMessages(handleMessage, currentThread.id);

    return () => {
      console.log(`<<< Desuscribiéndose de mensajes para thread: ${currentThread?.id}`);
      if (unsubscribe) unsubscribe();
    };
  }, [currentThread?.id, subscribeToMessages, addMessageFromWebSocket, setAgentStatusManually, setHasStoppedExecution]);

  // #274 — Suscripción al estado RAG por-adjunto (en vivo). Cuando N8N reporta
  // vectorización de un adjunto de chat, el backend emite `file_rag_status` al thread;
  // aquí se parchea el adjunto en la caché para que el thumbnail pase de "procesando"
  // a "listo"/"error"/aviso sin recargar.
  useEffect(() => {
    if (!currentThread?.id || !subscribeToFileRagStatus) return;

    // #1270 — la traducción evento → patch vive en `buildRagStatusPatch` (probada aparte):
    // aquí dentro no sería verificable sin montar medio chat.
    const handleFileRagStatus = (event) => {
      if (!event?.fileId) return;
      updateAttachmentRagStatus(event.fileId, buildRagStatusPatch(event));
    };

    const unsubscribe = subscribeToFileRagStatus(handleFileRagStatus, currentThread.id);
    return () => { if (unsubscribe) unsubscribe(); };
  }, [currentThread?.id, subscribeToFileRagStatus, updateAttachmentRagStatus]);

  // ── Streaming consumer (PRP-B): subscribe to all 6 stream-* events ──
  useEffect(() => {
    if (!currentThread?.id || !subscribeToStreamingEvents) return;
    dispatchStreaming({ type: 'CLEAR' }); // reset on thread change

    const onDelta = (payload) => {
      // 250 ms lockout: a delta arriving right after finish/cancel/error/persisted
      // is a straggler from N8N — discard so we don't orphan a new ghost bubble.
      if (Date.now() - lastFinishTsRef.current < 250) return;
      // Fallback: el fork actual sólo emite stream-tool-call-start cuando el
      // LLM stream entrega `stopReason='tool_use'`. Dentro de un Agent V2 de
      // N8N, el Agent loop consume las tool_call_chunks y el messageStop
      // antes de que lleguen al callback → en la práctica, el frontend nunca
      // recibe la metadata canónica vía tool-call-start. Pero `agentStatus`
      // (canal independiente, update_agent_status) sí trae agentName/color.
      // Sembrar desde ahí para que la burbuja en vivo nazca con avatar y
      // nombre correctos. Sticky-on-first del reducer respeta cualquier
      // valor canónico que llegue después por tool-call-start o agent-finish.
      const status = agentStatusRef.current;
      const fallbackAgentName =
        status?.agentName && status.agentName !== '__processing__'
          ? status.agentName
          : null;
      const fallbackColor = status?.color || null;
      dispatchStreaming({
        type: 'DELTA',
        delta: payload?.delta || '',
        done: payload?.done === true,
        fallbackAgentName,
        fallbackColor,
      });
    };

    const onToolCallStart = (payload) => {
      dispatchStreaming({
        type: 'TOOL_CALL_START',
        tools: payload?.tools || [],
        agentName: payload?.agentName,
        color: payload?.color,
      });
    };

    const onAgentFinish = () => {
      // agent_message will also clear the buffer; this is the safety net for
      // the case where the persisted message never arrives.
      lastFinishTsRef.current = Date.now();
      dispatchStreaming({ type: 'CLEAR' });
      setIsAnalyzingAttachments(false);
      if (analyzingTimeoutRef.current) { clearTimeout(analyzingTimeoutRef.current); analyzingTimeoutRef.current = null; }
    };

    const onCancelled = (payload) => {
      lastFinishTsRef.current = Date.now();
      // #494: inyectar el parcial persistido ANTES del CLEAR (sin flash de
      // burbuja vacía), marcado cancelledByUser para el separador ⏹ Detenido.
      // Sin messageId (STOP antes del primer token) solo se confirma y limpia.
      // La burbuja la construye `cancelledPartialMessage`, la misma que usa
      // useModularChat: las dos copias inline salían sin nombre ni color (#1558).
      const partial = cancelledPartialMessage(payload);
      if (partial) addMessageFromWebSocket(partial);
      confirmCancelled(payload);
      dispatchStreaming({ type: 'CLEAR' });
    };

    // #519: follow-up del corte cuando el turno llevaba un plan. Llega unos ms
    // después de onCancelled (la escritura en la auditoría no puede bloquear el
    // corte) y solo enriquece el pie ⏹ con el contador de pasos — no toca el
    // buffer de streaming.
    //
    // #898: el contador se pinta SOBRE EL MENSAJE que abrió el turno, no sobre
    // la cola del hilo. `triggerMessageId` viene ya normalizado por el backend
    // (sin el sufijo `::retry-<ts>`), así que apunta al mensaje real del
    // usuario. El backend además lo sella en BD cuando hubo pasos ejecutados;
    // este parche local es lo que hace que se vea AHORA, sin esperar un refetch
    // que este hook no hace (staleTime infinito, refetch automático apagado).
    const onCancelledPlan = (payload) => {
      if (!payload?.triggerMessageId || !payload?.executionId) {
        console.warn('[Chat] stream-cancelled-plan sin triggerMessageId — el contador de pasos no se pinta', payload);
        return;
      }
      applyStoppedPlan(payload.triggerMessageId, {
        executionId: payload.executionId,
        stepsCompleted: payload.stepsCompleted ?? 0,
        stepCount: payload.stepCount ?? null,
      });
    };

    const onError = (payload) => {
      console.error('[Chat] stream-error:', payload?.error);
      lastFinishTsRef.current = Date.now();
      dispatchStreaming({ type: 'CLEAR' });
      setStreamingError('Error en la respuesta del agente');
    };

    const onSnapshot = (payload) => {
      // Rehidratación (#368 caso 3): al (re)entrar en un thread con stream
      // vivo, el backend emite el buffer acumulado como PRIMER evento del
      // socket. SEED solo aplica sobre burbuja inexistente (el reducer lo
      // garantiza) y respeta el mismo lockout que los deltas — un snapshot
      // llegando justo tras el persist resucitaría contenido ya entregado.
      if (Date.now() - lastFinishTsRef.current < 250) return;
      dispatchStreaming({
        type: 'SEED',
        text: payload?.text,
        preToolText: payload?.preToolText,
        toolCalls: payload?.toolCalls,
        agentName: payload?.agentName ?? null,
        color: payload?.color ?? null,
      });
    };

    return subscribeToStreamingEvents(
      { onDelta, onToolCallStart, onAgentFinish, onCancelled, onCancelledPlan, onError, onSnapshot },
      currentThread.id
    );
  }, [currentThread?.id, subscribeToStreamingEvents, addMessageFromWebSocket, confirmCancelled, applyStoppedPlan]);

  // Interactive questions useEffect removed - feature deprecated

  // --- Scroll automático optimizado ---
  // Thread switch → instant jump (ChatGPT/Claude pattern); new message in
  // active thread → smooth animation.
  // #212: si el thread llega con `targetMessageId` (deep-link desde el detalle de
  // una ejecución de plan) el destino es ese mensaje, no el fondo. La decisión vive
  // aquí y solo aquí: cuando el scroll al mensaje estaba en ChatMessages, los
  // efectos del hijo corrían ANTES que este y el salto al fondo lo pisaba siempre.
  useEffect(() => {
    if (isLoadingMessages || messages.length === 0) return;

    // Ya decidimos el scroll para esta misma lista de mensajes (p. ej. en el
    // re-render que consume el deep-link): no volvemos a moverlo.
    if (lastScrolledMessagesRef.current === messages) return;
    lastScrolledMessagesRef.current = messages;

    const isThreadSwitch = lastScrolledThreadRef.current !== currentThread?.id;
    lastScrolledThreadRef.current = currentThread?.id;

    const targetMessageId = currentThread?.targetMessageId;
    const targetNode = targetMessageId
      ? document.querySelector(`[data-message-id="${targetMessageId}"]`)
      : null;

    if (targetNode) {
      targetNode.scrollIntoView({ behavior: 'instant', block: 'center' });
      setHighlightedMessageId(targetMessageId);
      clearTargetMessage();
      return;
    }

    messagesEndRef.current?.scrollIntoView({
      behavior: isThreadSwitch ? 'instant' : 'smooth'
    });
  }, [messages, isLoadingMessages, currentThread?.id, currentThread?.targetMessageId, clearTargetMessage]);

  // El resaltado del mensaje deep-linkeado es un destello, no un estado persistente.
  useEffect(() => {
    if (!highlightedMessageId) return;
    const timer = setTimeout(() => setHighlightedMessageId(null), 1600);
    return () => clearTimeout(timer);
  }, [highlightedMessageId]);

  // --- Auto-scroll durante el streaming ---
  // El texto del streaming vive en `streamingBuffer` (no en `messages`), así que
  // el efecto de arriba no se dispara token a token. Este lo sigue en vivo, pero
  // solo si el usuario está "pegado" al fondo (stick-to-bottom): si sube a leer,
  // no lo arrastramos. `behavior: 'auto'` evita animaciones smooth peleándose por token.
  const handleScrollContainerScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    stickToBottomRef.current =
      el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  }, []);

  // Callback ref: el Box scrollable es condicional (no existe en la welcome screen),
  // así que adjuntamos/desadjuntamos el listener justo cuando el nodo (des)monta.
  const setScrollContainer = useCallback((node) => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.removeEventListener('scroll', handleScrollContainerScroll);
    }
    scrollContainerRef.current = node;
    if (node) {
      node.addEventListener('scroll', handleScrollContainerScroll, { passive: true });
    }
  }, [handleScrollContainerScroll]);

  // Se sigue el final mientras solo hay actividad; en cuanto llega texto la ventana suelta y
  // el usuario lee a su ritmo. La regla vive en `streaming/scrollPolicy` y la comparten las
  // dos pilas de chat: tenerla duplicada es de donde salieron el indicador doble y el scroll
  // muerto del Builder.
  useEffect(() => {
    if (!stickToBottomRef.current) return;
    if (!debeSeguirElFinal(streamingBuffer)) return;
    messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
  }, [streamingBuffer]);

  // --- Memoización del contenido principal ---
  // Callback para cuando el usuario hace click en un ejemplo de prompt
  const handleExamplePromptClick = useCallback((prompt) => {
    console.log('[Chat] 💡 Example prompt clicked:', prompt);
    // Enviar el mensaje con filesWithThumbnails vacío
    handleSendMessage(prompt, []);
  }, [handleSendMessage]);

  // Cross-agent handoff: create thread tagged metadata.type='plan'.
  // Backend (THREAD_TYPE_ROUTERS) forces rol='Planner' so the Broker Switch
  // routes directly to the Planner workflow, bypassing LLM classification.
  const pendingAutoMessageRef = useRef(null);

  const handleHandoffAction = useCallback(async (action) => {
    if (action.type !== 'create-plan') return;

    try {
      // Build rich autoMessage from the user's actual last message + agent context
      const lastUserMsg = [...messages].reverse().find(m => m.sender === 'usuario');
      const userIntent = lastUserMsg?.content || 'Quiero crear un plan de trabajo.';
      const agentName = action.sourceAgentName || '';
      const autoMessage = agentName
        ? `${userIntent}\n\nContexto: estaba trabajando con ${agentName}.`
        : userIntent;

      const newThread = await threadService.createPlanThread();

      setThreads(prev => [newThread, ...prev]);
      selectThread(newThread);

      pendingAutoMessageRef.current = autoMessage;
    } catch (err) {
      console.error('[Chat] Error handling agent handoff:', err);
    }
  }, [selectThread, setThreads, messages]);

  // Send pending auto-message after WebSocket connects to the new thread
  useEffect(() => {
    if (pendingAutoMessageRef.current && currentThread) {
      const msg = pendingAutoMessageRef.current;
      pendingAutoMessageRef.current = null;
      waitForConnection(3000).then(() => {
        handleSendMessage(msg, []);
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentThread?.id]);

  const mainContent = useMemo(() => {
    // ✅ FIX: Also keep welcome screen during first-message transition to prevent flash.
    // Without this, flushSync(setCurrentThread) makes !currentThread false one frame
    // before the optimistic message arrives, briefly showing an empty chat view.
    if (messages.length === 0 && (!currentThread || isTransitioningToFirstMessageRef.current)) {
      return (
        <ChatWelcomeScreen
          onExampleClick={handleExamplePromptClick}
          role={selectedRole}
          actionCards={roleActionCards}
          onAgentClick={activeAgent ? handleAgentMenuOpen : undefined}
        >
          <ChatInput
              ref={chatInputRef}
              {...chatInputProps}
            />
        </ChatWelcomeScreen>
      );
    }

    return (
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          width: '100%'
        }}
      >
          {error && (
            <Alert
              severity="warning"
              sx={{ mb: 2, mx: 2 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => window.location.reload()}
                >
                  Reintentar
                </Button>
              }
            >
              {error.includes('Servidor no disponible')
                ? 'El servidor no está disponible o hay un problema con su conexión. Por favor, intente nuevamente.'
                : `Error de conexión: ${error}`
              }
            </Alert>
          )}

          <Box
            ref={setScrollContainer}
            sx={{
              flexGrow: 1,
              overflow: 'auto',
              bgcolor: 'background.paper',
              py: { xs: 0.5, md: 2 },
              width: '100%'
            }}
          >
            <ChatMessages
              messages={messages}
              isTyping={(isTyping || isAgentCurrentlyActive) && !streamingBuffer}
              theme={theme}
              messagesEndRef={messagesEndRef}
              threadId={currentThread?.id}
              highlightedMessageId={highlightedMessageId}
              // onAnswerQuestion removed - questions feature deprecated
              isLoadingMessages={isLoadingMessages}
              hasStoppedExecution={hasStoppedExecution}
              onRetry={handleRetry}
              isRetrying={isRetrying}
              userProfile={userProfile}
              userEmail={user?.email}
              agentStatus={agentStatus}
              onFeedbackChange={updateMessageFeedback}
              onHandoffAction={handleHandoffAction}
              isAnalyzingAttachments={isAnalyzingAttachments}
              streamingMessage={streamingBuffer}
              isStopping={isStoppingExecution}
              scrollContainerRef={scrollContainerRef}
            />
          </Box>
      </Box>
    );
  }, [
    messages,
    currentThread,
    chatInputProps,
    error,
    isTyping,
    theme,
    messagesEndRef,
    // handleAnswerQuestion removed - questions feature deprecated
    isLoadingMessages,
    agentStatus,
    hasStoppedExecution,
    handleRetry,
    isRetrying,
    userProfile,
    user?.email,
    handleExamplePromptClick,
    updateMessageFeedback,
    handleHandoffAction,
    selectedRole,
    isAnalyzingAttachments,
    streamingBuffer,
    isStoppingExecution,
    setScrollContainer,
    highlightedMessageId,
  ]);

  // Chat now renders inside AppShell (no header offset needed)
  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      width: '100%',
      bgcolor: theme.palette.background.default,
      overflow: 'hidden'
    }}>
      {/* Active agent indicator — clickable dropdown menu (ChatGPT pattern) */}
      {activeAgent && (
        <>
          <Box
            onClick={handleAgentMenuOpen}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              py: 0.75,
              px: 1.5,
              cursor: 'pointer',
              borderRadius: 1.5,
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            {(() => {
              const rc = prepareRoleColorForChat(activeAgent.color || theme.palette.primary.main, theme);
              return (
                <Avatar
                  sx={{
                    width: 26,
                    height: 26,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    background: rc.avatarBgGradient,
                    boxShadow: rc.avatarShadow,
                    color: rc.avatarText,
                  }}
                >
                  {getInitials(activeAgent.name)}
                </Avatar>
              );
            })()}
            <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 600, userSelect: 'none' }}>
              {activeAgent.name}
            </Typography>
            <KeyboardArrowDownIcon sx={{ fontSize: 18, color: 'text.disabled', ml: -0.25 }} />
          </Box>

          {/* Agent dropdown menu */}
          <Menu
            anchorEl={agentMenuAnchor}
            open={Boolean(agentMenuAnchor)}
            onClose={handleAgentMenuClose}
            TransitionProps={{ onExited: handleAgentMenuExited }}
            PaperProps={{
              elevation: 4,
              sx: {
                minWidth: 240,
                maxWidth: 320,
                overflow: 'visible',
                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.15))',
                mt: 0.5,
              },
            }}
            transformOrigin={{ horizontal: 'left', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
            sx={{ zIndex: Z_INDEX.USER_MENU_DROPDOWN }}
          >
            {/* Agent header in menu */}
            <Box sx={{ px: 2, py: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {(() => {
                  const rc = prepareRoleColorForChat(activeAgent.color || theme.palette.primary.main, theme);
                  return (
                    <Avatar
                      sx={{
                        width: 36,
                        height: 36,
                        fontSize: '0.9rem',
                        fontWeight: 600,
                        background: rc.avatarBgGradient,
                        boxShadow: rc.avatarShadow,
                        color: rc.avatarText,
                      }}
                    >
                      {getInitials(activeAgent.name)}
                    </Avatar>
                  );
                })()}
                <Box sx={{ overflow: 'hidden' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap>
                    {activeAgent.name}
                  </Typography>
                  {activeAgent.objectives && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        lineHeight: 1.3,
                      }}
                    >
                      {activeAgent.objectives}
                    </Typography>
                  )}
                  {reviewSummary && reviewSummary.totalReviews > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {'★'} {reviewSummary.averageRating} ({reviewSummary.totalReviews})
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>

            <Divider />

            {/* New chat */}
            <MenuItem onClick={handleAgentMenuNewChat}>
              <ListItemIcon>
                <AddCommentOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('chat.agentMenu.newChat', 'New chat')}</ListItemText>
            </MenuItem>

            {/* About */}
            <MenuItem onClick={handleAgentMenuAbout}>
              <ListItemIcon>
                <InfoOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('chat.agentMenu.about', 'About')}</ListItemText>
            </MenuItem>

            {/* Edit agent (owner only) */}
            {activeAgent.userId === user?.id && (
              <MenuItem onClick={handleAgentMenuEdit}>
                <ListItemIcon>
                  <TuneIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t('chat.agentMenu.editAgent', 'Edit agent')}</ListItemText>
              </MenuItem>
            )}

            {/* Collections */}
            <MenuItem onClick={handleAgentMenuCollections}>
              <ListItemIcon>
                <CollectionsBookmarkIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('chat.agentMenu.collections', 'Collections')}</ListItemText>
            </MenuItem>

            {/* Hide from sidebar */}
            <MenuItem onClick={handleAgentMenuToggleHidden}>
              <ListItemIcon>
                {hiddenAgentIds.has(activeAgent.id)
                  ? <VisibilityIcon fontSize="small" />
                  : <VisibilityOffIcon fontSize="small" />}
              </ListItemIcon>
              <ListItemText>
                {hiddenAgentIds.has(activeAgent.id)
                  ? t('chat.agentMenu.showInSidebar', 'Show in sidebar')
                  : t('chat.agentMenu.hideFromSidebar', 'Hide from sidebar')}
              </ListItemText>
            </MenuItem>

            {/* Leave a review */}
            <MenuItem onClick={handleAgentMenuReview}>
              <ListItemIcon>
                <StarOutlineIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('chat.agentMenu.leaveReview', 'Leave a review')}</ListItemText>
            </MenuItem>

            <Divider />

            {/* All agents */}
            <MenuItem onClick={handleAgentMenuGallery}>
              <ListItemIcon>
                <ExploreIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('chat.agentMenu.allAgents', 'All agents')}</ListItemText>
            </MenuItem>
          </Menu>

          {/* Agent review dialog */}
          <AgentReviewDialog
            open={reviewDialogOpen}
            onClose={() => setReviewDialogOpen(false)}
            role={activeAgent}
            existingReview={existingReview}
            onSubmit={handleReviewSubmit}
            onDelete={handleReviewDelete}
          />

          {/* Agent detail modal (About) */}
          <AgentDetailModal
            open={aboutModalOpen}
            onClose={() => setAboutModalOpen(false)}
            role={activeAgent}
            isOwned={activeAgent.userId === user?.id}
            onEdit={() => navigate(`/agents/builder/${activeAgent.id}`)}
          />
        </>
      )}

      <ChatDraftPendingStack
        drafts={draftsToShow}
        pendingActions={draftActions}
        onPublish={publishDraft}
        onDiscard={discardDraft}
        onView={handleViewDraft}
        onGetImpact={getDraftImpact}
      />

      {/* Main content area */}
      <Box sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        width: '100%',
        minHeight: 0 // Important for flex to work correctly
      }}>
        {mainContent}
      </Box>

      {/* Input at the bottom (only when there are messages or a thread) */}
      {(messages.length > 0 || currentThread) && (
        <Box sx={{
          flex: '0 0 auto',
          pb: { xs: 0, md: 3 } // Remove extra padding on mobile - ChatInput handles safe area
        }}>
          <ChatInput
              ref={chatInputRef}
              {...chatInputProps}
            />
        </Box>
      )}

      <Snackbar
        open={!!contextValidationError}
        autoHideDuration={5000}
        onClose={() => setContextValidationError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message={contextValidationError}
      />
      <Snackbar
        open={!!fileUploadError}
        autoHideDuration={5000}
        onClose={() => setFileUploadError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message={fileUploadError}
      />
      <Snackbar
        open={!!streamingError}
        autoHideDuration={5000}
        onClose={() => setStreamingError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message={streamingError}
      />
      <Snackbar
        open={!!draftError}
        autoHideDuration={5000}
        onClose={() => setDraftError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message={draftError}
      />
    </Box>
  );
};

export default Chat; 